import * as THREE from 'three';
import { perlin2 } from '../core/Rand.js';

/** Critically damped scalar spring. Semi-implicit Euler, stable at 120 Hz. */
export class Spring {
  constructor(omega = 18, value = 0) {
    this.w = omega;
    this.x = value;
    this.v = 0;
    this.target = value;
  }
  step(dt) {
    const w = this.w;
    this.v += (-2 * w * this.v - w * w * (this.x - this.target)) * dt;
    this.x += this.v * dt;
    return this.x;
  }
  set(v) { this.x = v; this.v = 0; this.target = v; }
  kick(v) { this.v += v; }
}

/** Critically damped 3-component spring. */
export class Spring3 {
  constructor(omega = 18) {
    this.w = omega;
    this.x = new THREE.Vector3();
    this.v = new THREE.Vector3();
    this.target = new THREE.Vector3();
  }
  step(dt) {
    const w = this.w;
    const t = _v3a.copy(this.x).sub(this.target).multiplyScalar(-w * w);
    t.addScaledVector(this.v, -2 * w);
    this.v.addScaledVector(t, dt);
    this.x.addScaledVector(this.v, dt);
    return this.x;
  }
  set(v) { this.x.copy(v); this.v.set(0, 0, 0); this.target.copy(v); }
  kick(v) { this.v.add(v); }
  reset() { this.x.set(0, 0, 0); this.v.set(0, 0, 0); this.target.set(0, 0, 0); }
}

const _v3a = new THREE.Vector3();

/** Layered 3D noise for breathing/idle drift. Deterministic in `t`. */
export function driftNoise(out, t, scale, seedOff = 0) {
  out.set(
    perlin2(t * 0.23 + seedOff, 11.7) * 0.62 + perlin2(t * 0.71 + seedOff, 3.1) * 0.28
      + perlin2(t * 1.63 + seedOff, 27.9) * 0.10,
    perlin2(t * 0.19 + seedOff, 41.3) * 0.62 + perlin2(t * 0.63 + seedOff, 17.5) * 0.28
      + perlin2(t * 1.41 + seedOff, 8.2) * 0.10,
    perlin2(t * 0.17 + seedOff, 63.9) * 0.62 + perlin2(t * 0.57 + seedOff, 55.1) * 0.28
      + perlin2(t * 1.29 + seedOff, 71.4) * 0.10,
  );
  return out.multiplyScalar(scale);
}

export const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
export const lerp = (a, b, t) => a + (b - a) * t;
export const smoothstep = (t) => { t = clamp01(t); return t * t * (3 - 2 * t); };
/** Ease used for weapon-switch and reload phase blending — quick out, soft in. */
export const easeOutCubic = (t) => { t = clamp01(t); const u = 1 - t; return 1 - u * u * u; };
export const easeInOutCubic = (t) => {
  t = clamp01(t);
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
};
export const easeOutBack = (t) => {
  t = clamp01(t);
  const c1 = 1.70158, c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
};

/**
 * A pose is a rigid transform plus a couple of scalar channels. Poses are blended
 * multiplicatively along the "base -> sprint -> ADS" chain and additively for bob,
 * sway, recoil and sequence offsets, so nothing ever fights anything else.
 */
export class Pose {
  constructor(pos = [0, 0, 0], rot = [0, 0, 0]) {
    this.pos = new THREE.Vector3(pos[0], pos[1], pos[2]);
    this.quat = new THREE.Quaternion().setFromEuler(new THREE.Euler(rot[0], rot[1], rot[2], 'XYZ'));
  }
  copy(p) { this.pos.copy(p.pos); this.quat.copy(p.quat); return this; }
  clone() { const p = new Pose(); return p.copy(this); }
  static blend(out, a, b, t) {
    out.pos.lerpVectors(a.pos, b.pos, t);
    out.quat.copy(a.quat).slerp(b.quat, t);
    return out;
  }
}

/**
 * Additive channel stack. Every animation source writes here rather than touching the
 * final transform, so the compositing order is one place and always the same.
 */
export class AdditiveStack {
  constructor() {
    this.pos = new THREE.Vector3();
    this.rot = new THREE.Vector3();  // radians, XYZ
    this.pivot = new THREE.Vector3();
  }
  clear() { this.pos.set(0, 0, 0); this.rot.set(0, 0, 0); this.pivot.set(0, 0, 0); }
  add(px, py, pz, rx, ry, rz) {
    this.pos.x += px; this.pos.y += py; this.pos.z += pz;
    this.rot.x += rx; this.rot.y += ry; this.rot.z += rz;
  }
  addVec(p, r, w = 1) {
    this.pos.addScaledVector(p, w);
    if (r) this.rot.addScaledVector(r, w);
  }
}

/** Frame-rate independent exponential approach. */
export function approach(cur, target, rate, dt) {
  return target + (cur - target) * Math.exp(-rate * dt);
}
